import {
  Flex,
  FormattedNumber,
  Icon,
  InternalAccessor,
  Spacing,
  TableCellContent,
  TableColumn,
  Text,
  Tooltip,
} from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Product } from '@features/products/types/product'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Currency } from '../../../../../types/currency'
import { LocaleIdentifier } from '../../../../../types/localeIdentifier'
import { DEFAULT_CURRENCY_NUMBER_FORMAT_OPTIONS } from '../../../constants/defaultCurrencyNumberFormatOptions'
import { ProductsTableHoverActions } from '../elements/ProductsTableHoverActions'
import { ProductsTableHoverActionHandler } from '../elements/ProductsTableHoverActions/types/productsTableHoverActionHandler'

interface UseProductsListColumnsProps {
  currency: Currency
  locale: LocaleIdentifier
  handleDeleteProductClick?: ProductsTableHoverActionHandler
  onDuplicateProductClick?: ProductsTableHoverActionHandler
  onEditProductClick?: ProductsTableHoverActionHandler
}

export const useProductsListColumns = ({
  currency,
  locale,
  handleDeleteProductClick,
  onDuplicateProductClick,
  onEditProductClick,
}: UseProductsListColumnsProps): { columns: TableColumn<Product>[] } => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const shouldShowVatInfo = !organization?.isVatExempted

  const vatRateColumns: TableColumn<Product>[] = useMemo(
    () => [
      {
        Header: t('products.table.column.price_excluding_vat'),
        accessor: 'price',
        Cell: ({ value }) => (
          <FormattedNumber.Currency
            currency={currency}
            value={value}
            locale={locale}
            options={DEFAULT_CURRENCY_NUMBER_FORMAT_OPTIONS}
          />
        ),
        alignment: 'right',
        sortable: true,
      },
      {
        Header: t('products.table.column.vat'),
        accessor: 'vatPct',
        Cell: ({ value }) => <FormattedNumber.Percent value={value} locale={locale} />,
        alignment: 'right',
        sortable: true,
      },
    ],
    [currency, locale, t],
  )

  return {
    columns: [
      {
        Header: t('products.table.column.name'),
        accessor: 'name',
        Cell: ({ value, row }) => {
          const shouldShowInternalNote = (row.original?.description?.length ?? 0) > 0
          return (
            <TableCellContent>
              <Flex alignItems="center" gap={Spacing.S8}>
                <Text truncate>{value}</Text>
                {shouldShowInternalNote && (
                  <Tooltip label={row.original.description}>
                    <Icon icon="receiptText" />
                  </Tooltip>
                )}
              </Flex>
            </TableCellContent>
          )
        },
        sortable: true,
      },
      ...(shouldShowVatInfo ? vatRateColumns : []),
      {
        Header: shouldShowVatInfo ? t('products.table.column.price_including_vat') : t('products.table.column.price'),
        accessor: 'total',
        Cell: ({ value }) => (
          <FormattedNumber.Currency
            currency={currency}
            value={value}
            locale={locale}
            options={DEFAULT_CURRENCY_NUMBER_FORMAT_OPTIONS}
          />
        ),
        alignment: 'right',
        sortable: true,
      },
      {
        accessor: InternalAccessor.HoverActions,
        Header: '',
        Cell: ({ row: { original } }) => (
          <ProductsTableHoverActions
            selectedProduct={original}
            onDeleteProductClick={handleDeleteProductClick}
            onDuplicateProductClick={onDuplicateProductClick}
            onEditProductClick={onEditProductClick}
          />
        ),
      },
    ],
  }
}
